import packageInfo from '../../../../package.json';
import appConfig from '../../../configs/appConfig';
import envVariables from '../../../consts/envVariables';
import {selectIsAuthorized, selectUserName} from '../../../state/selectors/auth';
import {selectDeviceCodentify, selectIotDeviceAssetId} from '../../../state/selectors/iotDevices';
import {getState} from '../../../state/store';
import CloudWatchClient from '../../aws/cloudWatch/CloudWatchClient';
import log from '../log';
import logLevels from '../logLevels';

let instance = null;

const LOG_COUNT_TO_PUSH = 10;
const PUSH_INTERVAL = 5 * 60 * 1000; // 5 minutes
const PUSH_INTERVAL_UNAUTHORIZED = 1 * 60 * 1000; // 1 minute
const PUSH_EVENTS_TIMEOUT = 1 * 1000; // 1 second
const PUSH_EVENTS_ON_FAILED_TIMEOUT = 10 * 1000; // 10 seconds

class CloudWatchLogger {
    constructor() {
        if (instance) {
            return instance;
        }

        instance = this;

        this.queue = [];
        this.lastPushTime = new Date().getTime();
        this.minLogLevel = appConfig.getCloudWatchLogConfig()?.level ?? logLevels.ERROR.name;
        this.level = logLevels[this.minLogLevel]?.name ?? logLevels.ERROR.name;
        this.cloudWatchClient = new CloudWatchClient();
        this.pushLogs();
    }

    getLevel = () => logLevels[this.level]?.id;
    trace = (msg, dusn) => this.log({level: logLevels.TRACE, msg, deviceId: dusn});
    debug = (msg, dusn) => this.log({level: logLevels.DEBUG, msg, deviceId: dusn});
    info = (msg, dusn) => this.log({level: logLevels.INFO, msg, deviceId: dusn});
    error = (msg, error, dusn) => this.log({level: logLevels.ERROR, msg, error, deviceId: dusn});
    warn = (msg, error, dusn) => this.log({level: logLevels.WARN, msg, error, deviceId: dusn});
    fatal = (msg, error, dusn) => this.log({level: logLevels.FATAL, msg, error, deviceId: dusn});

    log = ({level, msg = '', error, deviceId}) => {
        if (level.id < this.getLevel()) {
            return;
        }

        const logMessage = this.getLogFields(level.name, msg, error, deviceId);

        this.addToQueue(logMessage);
    };

    addToQueue = (message) =>
        this.queue.push({
            message,
            timestamp: new Date().getTime(),
        });

    pushLogs = async () => {
        const isUserAuthorized = selectIsAuthorized(getState());

        const time = this.lastPushTime + (isUserAuthorized ? PUSH_INTERVAL : PUSH_INTERVAL_UNAUTHORIZED);

        try {
            //PUSH logs, if the log queue contains 10 items or the time since the last push is greater than PUSH_INTERVAL
            if (this.queue.length !== 0 && (this.queue.length >= LOG_COUNT_TO_PUSH || time < new Date())) {
                const logsToPush = this.queue.slice(0, LOG_COUNT_TO_PUSH);

                await this.cloudWatchClient.putLogs(logsToPush);

                this.queue.splice(0, LOG_COUNT_TO_PUSH);

                this.lastPushTime = new Date().getTime();
            }

            setTimeout(() => this.pushLogs(), PUSH_EVENTS_TIMEOUT);
        } catch (e) {
            log.error(`Failed to push logs, retrying in ${PUSH_EVENTS_ON_FAILED_TIMEOUT / 1000} seconds`, e.message);

            setTimeout(() => this.pushLogs(), PUSH_EVENTS_ON_FAILED_TIMEOUT);
        }
    };

    collectData = (serialNumber) => {
        const env = this.getLogValue(envVariables.ENV);
        const appVersion = this.getLogValue(packageInfo?.version);
        const appName = this.getLogValue(envVariables.APP_TYPE);
        const market = this.getLogValue(appConfig.getMarket());
        const cognitoUser = this.getLogValue(selectUserName(getState()));
        const dusn = this.getLogValue(serialNumber);
        const assetId = this.getLogValue(selectIotDeviceAssetId(serialNumber)(getState()));
        const codentify = this.getLogValue(selectDeviceCodentify(serialNumber)(getState()));
        const logStreamName = this.cloudWatchClient.getLogStreamName();

        return {
            env,
            appName,
            appVersion,
            market,
            cognitoUser,
            dusn,
            assetId,
            codentify,
            logStreamName,
        };
    };

    getLogValue = (prop) => (prop ? prop : 'n/a');

    getLogFields = (level, msg, error, dusn) => {
        const collectData = this.collectData(dusn);
        const date = new Date().toISOString();

        const data = {date, level, message: msg, error, ...collectData};

        return JSON.stringify(data);
    };
}

export default CloudWatchLogger;
